import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Tooltip,
  TablePagination,
  FormControlLabel,
  Switch,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const ORDER_STATUS = {
  NEW: { value: 'new', label: 'Nowe', color: 'info' },
  IN_PROGRESS: { value: 'in_progress', label: 'W realizacji', color: 'warning' },
  READY: { value: 'ready', label: 'Gotowe', color: 'success' },
  CANCELLED: { value: 'cancelled', label: 'Anulowane', color: 'error' }
};

const Orders = () => {
  const { currentUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = currentUser?.role === 'admin';

  const [orders, setOrders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    product_description: '',
    estimated_completion: '',
    deposit_amount: '',
    has_deposit: false,
    notes: '',
    status: 'new',
    total_amount: '',
    remaining_amount: ''
  });

  const fetchOrders = useCallback(async () => {
    try {
      // Sprawdzenie, czy użytkownik jest zalogowany
      if (!currentUser || !currentUser.uid) {
        console.log('Brak zalogowanego użytkownika lub brak UID');
        return;
      }

      console.log('Pobieranie zamówień dla użytkownika:', currentUser.uid, 'Rola:', currentUser.role);
      
      // Tworzenie pustej kolekcji, jeśli nie istnieje
      const ordersCollection = collection(db, 'orders');
      
      // Najpierw spróbujmy pobrać wszystkie zamówienia bez filtrów
      let ordersQuery;
      
      if (isAdmin) {
        console.log('Użytkownik jest adminem, pobieranie wszystkich zamówień');
        // Admin widzi wszystkie zamówienia
        ordersQuery = query(ordersCollection);
      } else {
        console.log('Użytkownik nie jest adminem, pobieranie tylko jego zamówień');
        // Zwykli użytkownicy widzą tylko swoje zamówienia
        ordersQuery = query(ordersCollection, where('employee_id', '==', currentUser.uid));
      }
      
      const ordersSnapshot = await getDocs(ordersQuery);
      console.log('Pobrano zamówienia, ilość:', ordersSnapshot.docs.length);
      
      const ordersList = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          created_at: data.created_at instanceof Timestamp ? data.created_at.toDate() : new Date(),
          estimated_completion: data.estimated_completion instanceof Timestamp ? 
            data.estimated_completion.toDate() : new Date()
        };
      });
      
      console.log('Przetworzone zamówienia:', ordersList);
      setOrders(ordersList);
    } catch (error) {
      console.error('Błąd podczas pobierania zamówień:', error);
      enqueueSnackbar('Błąd podczas pobierania zamówień', { variant: 'error' });
    }
  }, [enqueueSnackbar, isAdmin, currentUser]);

  const fetchEmployees = useCallback(async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersQuery = query(usersCollection, where('role', 'in', ['user', 'advisor']));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania listy pracowników:', error);
      enqueueSnackbar('Błąd podczas pobierania listy pracowników', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Usunięto funkcję initializeOrdersCollection

  useEffect(() => {
    if (currentUser) {
      // Pobieramy zamówienia i pracowników
      fetchOrders();
      fetchEmployees();
    }
  }, [fetchOrders, fetchEmployees, currentUser]);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
    setFormData({
      client_name: '',
      client_phone: '',
      product_description: '',
      estimated_completion: '',
      deposit_amount: '',
      has_deposit: false,
      notes: '',
      status: 'new',
      total_amount: '',
      remaining_amount: ''
    });
  };

  const handleAddOrder = () => {
    setOpenDialog(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setFormData({
      client_name: order.client_name,
      client_phone: order.client_phone,
      product_description: order.product_description,
      estimated_completion: format(order.estimated_completion, 'yyyy-MM-dd'),
      deposit_amount: order.deposit_amount || '',
      has_deposit: Boolean(order.has_deposit),
      notes: order.notes || '',
      status: order.status,
      total_amount: order.total_amount || '',
      remaining_amount: order.remaining_amount || ''
    });
    setOpenDialog(true);
  };

  // Funkcja do obliczania pozostałej kwoty do zapłaty
  const calculateRemainingAmount = (total, deposit) => {
    if (!total) return '';
    const totalAmount = parseFloat(total) || 0;
    const depositAmount = parseFloat(deposit) || 0;
    return (totalAmount - depositAmount).toString();
  };

  const handleSubmit = async () => {
    try {
      const orderData = {
        ...formData,
        employee_id: selectedOrder ? selectedOrder.employee_id : currentUser.uid,
        employee_name: selectedOrder ? selectedOrder.employee_name : currentUser.displayName,
        created_at: selectedOrder ? selectedOrder.created_at : Timestamp.now(),
        updated_at: Timestamp.now(),
        estimated_completion: Timestamp.fromDate(new Date(formData.estimated_completion)),
        deposit_amount: formData.has_deposit ? formData.deposit_amount : '',
        total_amount: formData.total_amount || '',
        remaining_amount: calculateRemainingAmount(formData.total_amount, formData.deposit_amount)
      };

      if (selectedOrder) {
        // Aktualizacja istniejącego zamówienia
        const orderRef = doc(db, 'orders', selectedOrder.id);
        await updateDoc(orderRef, orderData);
        enqueueSnackbar('Zamówienie zaktualizowane pomyślnie', { variant: 'success' });
      } else {
        // Dodanie nowego zamówienia
        await addDoc(collection(db, 'orders'), orderData);
        enqueueSnackbar('Zamówienie dodane pomyślnie', { variant: 'success' });
      }

      handleDialogClose();
      fetchOrders();
    } catch (error) {
      console.error('Błąd podczas zapisywania zamówienia:', error);
      enqueueSnackbar('Błąd podczas zapisywania zamówienia', { variant: 'error' });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Czy na pewno chcesz usunąć to zamówienie?')) {
      try {
        const orderRef = doc(db, 'orders', orderId);
        await deleteDoc(orderRef);
        enqueueSnackbar('Zamówienie usunięte pomyślnie', { variant: 'success' });
        fetchOrders();
      } catch (error) {
        console.error('Błąd podczas usuwania zamówienia:', error);
        enqueueSnackbar('Błąd podczas usuwania zamówienia', { variant: 'error' });
      }
    }
  };

  // Obsługa zmiany kwoty całkowitej lub zadatku
  const handleAmountChange = (field, value) => {
    const updates = { [field]: value };
    
    if (field === 'total_amount' && formData.has_deposit) {
      updates.remaining_amount = calculateRemainingAmount(value, formData.deposit_amount);
    } else if (field === 'deposit_amount' && formData.has_deposit) {
      updates.remaining_amount = calculateRemainingAmount(formData.total_amount, value);
    }
    
    setFormData({ ...formData, ...updates });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calculateFutureDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const predefinedDates = [
    { label: '14 dni', value: 14 },
    { label: '30 dni', value: 30 },
    { label: '90 dni', value: 90 }
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {isAdmin ? 'Wszystkie zamówienia' : 'Moje zamówienia'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {isAdmin && (
            <>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Pracownik</InputLabel>
                <Select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  label="Pracownik"
                >
                  <MenuItem value="">Wszyscy</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={true}
                    onChange={(e) => {}}
                  />
                }
                label="Pokaż wszystkie"
              />
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddOrder}
          >
            Dodaj zamówienie
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Klient</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell>Data realizacji</TableCell>
              <TableCell>Łączna kwota</TableCell>
              <TableCell>Zadatek</TableCell>
              <TableCell>Pozostało</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Uwagi</TableCell>
              {isAdmin && <TableCell>Pracownik</TableCell>}
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow key={order.id}>
                  <TableCell>{order.client_name}</TableCell>
                  <TableCell>{order.client_phone}</TableCell>
                  <TableCell>{order.product_description}</TableCell>
                  <TableCell>
                    {format(order.estimated_completion, 'dd.MM.yyyy', { locale: pl })}
                  </TableCell>
                  <TableCell>
                    {order.total_amount ? `${order.total_amount} zł` : '-'}
                  </TableCell>
                  <TableCell>
                    {order.has_deposit && order.deposit_amount ? `${order.deposit_amount} zł` : '-'}
                  </TableCell>
                  <TableCell>
                    {order.remaining_amount ? `${order.remaining_amount} zł` : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ORDER_STATUS[order.status.toUpperCase()]?.label || 'Nowe'}
                      color={ORDER_STATUS[order.status.toUpperCase()]?.color || 'info'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{order.notes || '-'}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      {order.employee_name || (order.employee ? `${order.employee.first_name} ${order.employee.last_name}` : '-')}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="Edytuj">
                      <IconButton onClick={() => handleEditOrder(order)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Usuń">
                      <IconButton onClick={() => handleDeleteOrder(order.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={orders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedOrder ? 'Edytuj zamówienie' : 'Dodaj nowe zamówienie'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nazwa klienta"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Telefon"
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Planowana data realizacji</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setFormData({ ...formData, estimated_completion: '' });
                    } else {
                      setFormData({ ...formData, estimated_completion: calculateFutureDate(e.target.value) });
                    }
                  }}
                  label="Planowana data realizacji"
                >
                  {predefinedDates.map((date) => (
                    <MenuItem key={date.value} value={date.value}>
                      {date.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">Inna data</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.estimated_completion || formData.status === 'custom' ? (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Wybrana data realizacji"
                  value={formData.estimated_completion}
                  onChange={(e) => setFormData({ ...formData, estimated_completion: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Opis produktu"
                value={formData.product_description}
                onChange={(e) => setFormData({ ...formData, product_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Uwagi"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  {Object.values(ORDER_STATUS).map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Sekcja płatności */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Informacje o płatności
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Łączna kwota zamówienia"
                type="number"
                value={formData.total_amount}
                onChange={(e) => handleAmountChange('total_amount', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">PLN </InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.has_deposit}
                    onChange={(e) => {
                      const hasDeposit = e.target.checked;
                      const updates = { 
                        has_deposit: hasDeposit,
                        deposit_amount: hasDeposit ? formData.deposit_amount : ''
                      };
                      if (hasDeposit && formData.total_amount) {
                        updates.remaining_amount = calculateRemainingAmount(formData.total_amount, updates.deposit_amount);
                      }
                      setFormData({ ...formData, ...updates });
                    }}
                  />
                }
                label="Zadatek"
              />
              {formData.has_deposit && (
                <TextField
                  fullWidth
                  label="Kwota zadatku"
                  type="number"
                  value={formData.deposit_amount}
                  onChange={(e) => handleAmountChange('deposit_amount', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">PLN </InputAdornment>,
                  }}
                />
              )}
            </Grid>
            
            {formData.has_deposit && formData.total_amount && formData.deposit_amount && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pozostało do zapłaty"
                  type="number"
                  value={formData.remaining_amount}
                  disabled
                  InputProps={{
                    startAdornment: <InputAdornment position="start">PLN </InputAdornment>,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} startIcon={<ClearIcon />}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<CheckIcon />}>
            {selectedOrder ? 'Zapisz zmiany' : 'Dodaj zamówienie'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
